// BlueprintImportUpload — a dropped `.aosbp.json` becomes a NEW blueprint card.
//
// No form: everything the row needs is in the file (kind, graph, declarations,
// its original name), and `uniqueAssetName` inside createBlueprintAsset makes a
// repeat import a second card rather than a clash. Same shape as
// EnvironmentDirectUpload — upload immediately, edit in the inspector after the
// row exists.
//
// It is deliberately ADDITIVE and never sets the imported card as default.
// Import creating a new row is the entire fix for the wholesale-overwrite the
// legacy File-menu import performed; making it the character's loaded graph as
// a side effect of a drop would put that overwrite straight back.

import { useEffect, useRef, useState } from 'react'
import { readBlueprintImportFile, importBlueprintEnvelope } from '../../../lib/blueprintAssetTransfer'
import { blueprintGraphLabel } from '../../../lib/assetKindColors'
import WizardShell from './WizardShell'

export default function BlueprintImportUpload({ file, path = [], onDone, onCancel }) {
  const [state, setState] = useState({ phase: 'reading' }) // reading | importing | error
  // A dropped queue can re-render this component; the import must run once.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    let cancelled = false

    ;(async () => {
      const parsed = await readBlueprintImportFile(file)
      if (cancelled) return
      if (!parsed.ok) {
        setState({ phase: 'error', reason: parsed.reason })
        return
      }
      setState({ phase: 'importing', envelope: parsed.envelope })
      try {
        const folderPath = path.length ? '/' + path.join('/') : '/blueprints'
        const { row } = await importBlueprintEnvelope(parsed.envelope, { folderPath })
        if (cancelled) return
        onDone?.({ row, envelope: parsed.envelope })
      } catch (e) {
        if (cancelled) return
        setState({ phase: 'error', reason: e?.message ?? String(e) })
      }
    })()

    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (state.phase === 'error') {
    return (
      <WizardShell title="Import blueprint" onCancel={onCancel} error={state.reason}>
        <div data-testid="blueprint-import-error" style={{ font: '11px/1.5 var(--m-font)', color: 'var(--m-text)' }}>
          <strong>{file?.name}</strong> was not imported.
        </div>
      </WizardShell>
    )
  }

  const kindLabel = state.envelope ? blueprintGraphLabel(state.envelope.kind) : null
  return (
    <WizardShell title="Import blueprint" onCancel={onCancel}>
      <div data-testid="blueprint-import-progress" style={{ font: '11px/1.5 var(--m-font)', color: 'var(--m-text)' }}>
        {state.phase === 'reading'
          ? `Reading ${file?.name}…`
          : `Creating a new ${kindLabel ?? state.envelope?.kind} card from ${file?.name}…`}
      </div>
    </WizardShell>
  )
}
